import React from 'react';
import moment from 'moment';

import { DataTable, ColumnConfig } from '../../components/Table';
import { CardTitle } from '../../components/Card';
import Pagination from '../../components/Pagination/Pagination';
import PatchDetailsModal from '../../components/Modal/PatchDetailsModal';
import { cveTheme, getCveCvssColor, getCveSeverityStyle } from '../../common/themes/cveColors';
import { PolicyMode } from '../../components/services/softwareComplianceService';

import { Asset, BannerCard, SoftwareSummary } from './infoTableTypes';
import { usePermissions } from '../../hooks/usePermissions';

// ================= CVE HELPERS =================
const SeverityBadge = ({ severity }: { severity: string }) => {
  const style = getCveSeverityStyle(severity);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider border"
      style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
    >
      {severity}
    </span>
  );
};

// ================= PROPS =================
export interface InfoViewProps {
  softwareInfo: SoftwareSummary;
  bannerCards: BannerCard[];
  bannerColumns: number;
  hasSoftwarePolicy: boolean;
  softwarePolicyMode: PolicyMode | null;
  rowDetails: any;
  tableData: Asset[];
  columns: ColumnConfig[];
  loading: boolean;
  showActiveOnly: boolean;
  showPatchModal: boolean;
  selectedRow: any;
  currentPage: number;
  totalPages: number;
  selectedItemIds: string[];
  onActiveFilter: () => void;
  onOpenRemediateModal: () => void;
  onPageChange: (page: number) => void;
  onCloseModal: () => void;
  onDownloadCSV: () => void;
}

// ================= COMPONENT =================
const InfoView: React.FC<InfoViewProps> = ({
  softwareInfo,
  bannerCards,
  bannerColumns,
  hasSoftwarePolicy,
  softwarePolicyMode,
  rowDetails,
  tableData,
  columns,
  loading,
  showActiveOnly,
  showPatchModal,
  selectedRow,
  currentPage,
  totalPages,
  selectedItemIds,
  onActiveFilter,
  onOpenRemediateModal,
  onPageChange,
  onCloseModal,
  onDownloadCSV,
}) => {
  const { canEdit } = usePermissions();
  const canRemediate = canEdit('remediationPlan');

  return (
    <div className="p-4 bg-black rounded text-white">
      {/* HEADER */}
      <div className="mb-6">
        {softwareInfo ? (
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold">{softwareInfo.softwareAssetName}</h2>
            {hasSoftwarePolicy && (
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold tracking-[0.08em] ${
                  softwarePolicyMode === PolicyMode.Allowlist
                    ? 'border-[#00C16A] bg-[#06281A] text-[#38FFB3]'
                    : softwarePolicyMode === PolicyMode.Partial
                      ? 'border-[#D6A300] bg-[#2C2206] text-[#FFD66B]'
                      : 'border-[#D12B2B] bg-[#2A0B0B] text-[#FF8A8A]'
                }`}
              >
                {softwarePolicyMode === PolicyMode.Allowlist
                  ? 'ALLOWED'
                  : softwarePolicyMode === PolicyMode.Partial
                    ? 'PARTIAL'
                    : 'BLOCKED'}
              </span>
            )}
          </div>
        ) : (
          <div className="h-10 w-64 bg-gray-700 animate-pulse rounded mb-4"></div>
        )}

        {/* CARDS */}
        <div className="grid gap-4 w-full" style={{ gridTemplateColumns: `repeat(${bannerColumns}, minmax(0, 1fr))` }}>
          {softwareInfo
            ? bannerCards.map((card, i) => (
                <div
                  key={i}
                  style={{ backgroundColor: i % 2 === 0 ? '#2E2D2D' : '#1D1D1D' }}
                  className="h-[120px] p-4 rounded-lg shadow flex flex-col overflow-hidden"
                >
                  <div className="mb-2">
                    <CardTitle title={card.label} />
                  </div>
                  <div className="flex-1 flex items-center">
                    <span className="text-3xl font-bold ml-[28px] truncate w-[90%]">{card.value}</span>
                  </div>
                  {card.date && (
                    <div className="mt-auto">
                      <span className="text-xs text-gray-400 ml-[28px] opacity-70">
                        {card.date && !card.date.startsWith('0001-01-01') ? moment(card.date).format('DD MMM YYYY') : '-'}
                      </span>
                    </div>
                  )}
                </div>
              ))
            : Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[120px] p-4 rounded-lg shadow flex flex-col animate-pulse"
                  style={{ backgroundColor: i % 2 === 0 ? '#2E2D2D' : '#1D1D1D' }}
                >
                  <div className="h-6 w-24 bg-gray-600 rounded mb-2"></div>
                  <div className="flex-1 flex items-center">
                    <div className="h-10 w-32 bg-gray-500 rounded ml-[28px]"></div>
                  </div>
                  <div className="mt-auto">
                    <div className="h-3 w-20 bg-gray-500 rounded ml-[28px] opacity-70"></div>
                  </div>
                </div>
              ))}
        </div>

        {/* CVE DETAILS — shown when rowDetails is present */}
        {rowDetails && (
          <div
            className="mt-5 flex items-center justify-between w-full px-4 py-3 rounded-lg"
            style={{ backgroundColor: cveTheme.panelBackground, border: `1px solid ${cveTheme.panelBorder}` }}
          >
            {/* CVE ID link */}
            <a
              href={rowDetails.cveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-mono font-semibold text-sm transition-colors"
              style={{ color: cveTheme.link }}
            >
              {rowDetails.cveId}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3 h-3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>

            <span className="w-px h-5 bg-[#333]" />

            {/* Severity badge */}
            <SeverityBadge severity={rowDetails.severity} />

            <span className="w-px h-5 bg-[#333]" />

            {/* CVSS */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: cveTheme.label }}>
                CVSS
              </span>
              <span className="text-sm font-bold tabular-nums" style={{ color: getCveCvssColor(rowDetails.cvssScore) }}>
                {rowDetails.cvssScore?.toFixed(1)}
              </span>
              <span className="text-[11px]" style={{ color: cveTheme.labelMuted }}>
                / 10
              </span>
            </div>

            <span className="w-px h-5 bg-[#333]" />

            {/* EPSS */}
            {rowDetails.epssScore != null && (
              <>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: cveTheme.label }}>
                    EPSS
                  </span>
                  <span className="text-sm font-bold tabular-nums" style={{ color: cveTheme.epss }}>
                    {(rowDetails.epssScore * 100).toFixed(2)}%
                  </span>
                </div>
                <span className="w-px h-5 bg-[#333]" />
              </>
            )}

            {/* KEV */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: cveTheme.label }}>
                KEV
              </span>
              {rowDetails.kev ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: cveTheme.kevListed }}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: cveTheme.kevDot }} />
                  Listed
                </span>
              ) : (
                <span className="text-xs font-medium" style={{ color: cveTheme.kevNotListed }}>
                  Not listed
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ACTIONS */}
      <div className="flex gap-2 justify-end mb-5">
        {canRemediate && softwareInfo.packageIdentifier && softwareInfo.latestPackageVersion !== softwareInfo.softwareAssetVersion && (
          <button
            className={`px-4 py-2 rounded text-sm ${
              selectedItemIds.length === 0 ? 'bg-gray-600 opacity-50' : 'bg-red-600 hover:bg-red-700'
            }`}
            onClick={onOpenRemediateModal}
            disabled={selectedItemIds.length === 0}
          >
            Remediate
          </button>
        )}

        <button type="button" className="bg-green-600 px-4 py-2 rounded" onClick={onDownloadCSV} disabled={loading}>
          Download CSV
        </button>

        <button className="bg-green-600 px-4 py-2 rounded" onClick={onActiveFilter}>
          {showActiveOnly ? 'Show All' : 'Active'}
        </button>
      </div>

      {/* TABLE */}
      <DataTable columns={columns} data={tableData} getRowId={(item: any) => item.tenantAgentID} />

      {/* PAGINATION */}
      <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={onPageChange} />

      {/* MODAL */}
      {showPatchModal && (
        <PatchDetailsModal
          show={showPatchModal}
          selectedRow={selectedRow}
          heartbeatResponse={null}
          isLoading={false}
          onClose={onCloseModal}
        />
      )}
    </div>
  );
};

export default InfoView;
